import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { toast } from "sonner";
import {
  Calendar, CurrencyDollar, Coins, Flask, Plus, Trash, FloppyDisk,
  Warning, Airplane, Tag, Users,
} from "@phosphor-icons/react";

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  // input type=datetime-local expects "YYYY-MM-DDTHH:mm" in local time
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocal = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return d.toISOString();
};

const Section = ({ title, subtitle, icon: Icon, children }) => (
  <div className="bg-[#111827] border border-white/10 rounded-lg p-5 mb-5">
    <div className="flex items-start gap-3 mb-4">
      {Icon && <Icon size={22} weight="duotone" className="text-blue-400 mt-0.5" />}
      <div>
        <p className="font-display text-base font-bold text-white">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[11px] font-medium text-gray-400 mb-1.5 uppercase tracking-wider">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const inputCls =
  "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/campaign-settings");
      setSettings(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const updateMilestone = (idx, patch) => {
    const next = [...(settings.milestones || [])];
    next[idx] = { ...next[idx], ...patch };
    update("milestones", next);
  };

  const addMilestone = () => {
    const list = [...(settings.milestones || [])];
    const highest = list.length ? Math.max(...list.map((m) => m.lot)) : 0;
    list.push({ lot: highest + 10, reward: 0, label: "" });
    update("milestones", list);
  };

  const removeMilestone = (idx) => {
    const next = [...(settings.milestones || [])];
    next.splice(idx, 1);
    update("milestones", next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        campaign_name: settings.campaign_name,
        campaign_start: settings.campaign_start,
        campaign_end: settings.campaign_end,
        target_participants: Number(settings.target_participants),
        rebate_per_lot: Number(settings.rebate_per_lot),
        bali_trip_cost_per_qualifier: Number(settings.bali_trip_cost_per_qualifier),
        balance_threshold_usd: Number(settings.balance_threshold_usd ?? 100),
        admin_telegram_url: settings.admin_telegram_url || "",
        admin_telegram_label: settings.admin_telegram_label || "",
        milestones: (settings.milestones || []).map((m) => ({
          lot: Number(m.lot),
          reward: Number(m.reward || 0),
          label: m.label || null,
        })).filter((m) => m.lot > 0),
        campaign_symbol_keywords: (settings.campaign_symbol_keywords || []).map((k) => String(k).trim()).filter(Boolean),
        test_mode_enabled: !!settings.test_mode_enabled,
        test_mode_note: settings.test_mode_note ?? "",
      };
      const { data } = await api.put("/campaign-settings", payload);
      setSettings(data);
      toast.success("Campaign settings updated");
      // Signal AppLayout to refresh test-mode banner immediately
      window.dispatchEvent(new Event("campaign-settings-updated"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-400">Loading settings…</div>;
  if (!settings) return <div className="p-8 text-red-400">Failed to load settings</div>;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <PageHeader
        title="Campaign Settings"
        subtitle="Live-tune the challenge without a deploy · Super Admin only"
        breadcrumbs="Settings / Campaign"
        testId="settings-page"
        actions={
          <button
            data-testid="settings-save-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md shadow-[0_4px_20px_rgba(37,99,235,0.3)] transition-colors"
          >
            <FloppyDisk size={16} weight="duotone" />
            {saving ? "Saving…" : "Save changes"}
          </button>
        }
      />

      {/* Test mode */}
      <Section
        title="Test Mode"
        subtitle="Accept trades outside the campaign window for end-to-end testing. Out-of-window trades are stored, flagged, but EXCLUDED from milestone counts."
        icon={Flask}
      >
        <div className="flex items-start gap-4 p-4 rounded-md border border-orange-500/30 bg-orange-500/5">
          <div className="flex items-center gap-3">
            <button
              data-testid="test-mode-toggle"
              onClick={() => update("test_mode_enabled", !settings.test_mode_enabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${settings.test_mode_enabled ? "bg-orange-500" : "bg-gray-700"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${settings.test_mode_enabled ? "translate-x-6" : ""}`} />
            </button>
            <span className="text-sm font-medium text-white">
              {settings.test_mode_enabled ? "ON" : "OFF"}
            </span>
          </div>
          <div className="flex-1">
            {settings.test_mode_enabled && (
              <div className="flex items-start gap-2 text-orange-300 text-xs">
                <Warning size={14} className="mt-0.5" weight="fill" />
                <span>Active: A global banner will appear on every page. Trades outside the campaign window will be marked with an <code>OUT-OF-WINDOW</code> badge and skipped for milestone calculations.</span>
              </div>
            )}
            <input
              type="text"
              placeholder="Optional note (e.g. 'Broker sandbox test — 10 Feb 2026')"
              value={settings.test_mode_note || ""}
              onChange={(e) => update("test_mode_note", e.target.value)}
              className={`${inputCls} mt-3`}
              data-testid="test-mode-note"
            />
          </div>
        </div>
      </Section>

      {/* Dates */}
      <Section title="Campaign Window" subtitle="Trade close-time must fall inside this window to count." icon={Calendar}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Campaign Name">
            <input
              data-testid="setting-campaign-name"
              type="text"
              value={settings.campaign_name || ""}
              onChange={(e) => update("campaign_name", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Start (local)">
            <input
              data-testid="setting-campaign-start"
              type="datetime-local"
              value={toDatetimeLocal(settings.campaign_start)}
              onChange={(e) => update("campaign_start", fromDatetimeLocal(e.target.value))}
              className={inputCls}
            />
          </Field>
          <Field label="End (local)">
            <input
              data-testid="setting-campaign-end"
              type="datetime-local"
              value={toDatetimeLocal(settings.campaign_end)}
              onChange={(e) => update("campaign_end", fromDatetimeLocal(e.target.value))}
              className={inputCls}
            />
          </Field>
        </div>
      </Section>

      {/* Targets & rebates */}
      <Section title="Targets & Rebates" icon={CurrencyDollar}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Target Participants" hint="Grand target head-count">
            <div className="relative">
              <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="setting-target-participants"
                type="number"
                value={settings.target_participants ?? 0}
                onChange={(e) => update("target_participants", e.target.value)}
                className={`${inputCls} pl-8`}
              />
            </div>
          </Field>
          <Field label="Rebate per Lot (USD)" hint="Revenue booked per valid lot">
            <div className="relative">
              <CurrencyDollar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="setting-rebate"
                type="number"
                step="0.01"
                value={settings.rebate_per_lot ?? 0}
                onChange={(e) => update("rebate_per_lot", e.target.value)}
                className={`${inputCls} pl-8`}
              />
            </div>
          </Field>
          <Field label="Bali Trip Cost / Qualifier" hint="Estimated USD spend per Bali qualifier">
            <div className="relative">
              <Airplane size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="setting-bali-cost"
                type="number"
                step="0.01"
                value={settings.bali_trip_cost_per_qualifier ?? 0}
                onChange={(e) => update("bali_trip_cost_per_qualifier", e.target.value)}
                className={`${inputCls} pl-8`}
              />
            </div>
          </Field>
          <Field
            label="Balance Threshold (USD)"
            hint="Display value only. Broker must set the actual threshold on XM's side. Sync will flag participants whose broker balance ≥ this value."
          >
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
              <input
                data-testid="setting-balance-threshold"
                type="number"
                step="0.01"
                value={settings.balance_threshold_usd ?? 100}
                onChange={(e) => update("balance_threshold_usd", e.target.value)}
                className={`${inputCls} pl-8`}
              />
            </div>
          </Field>
          <Field
            label="Admin Telegram URL"
            hint="Public 'Contact Admin' link shown on the leaderboard CTA. Must be a valid t.me/… or https://t.me/… URL."
          >
            <input
              data-testid="setting-admin-telegram-url"
              type="url"
              placeholder="https://t.me/mbealert"
              value={settings.admin_telegram_url ?? ""}
              onChange={(e) => update("admin_telegram_url", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Admin Telegram Button Label" hint="Optional. Overrides the default 'Contact Admin' CTA text.">
            <input
              data-testid="setting-admin-telegram-label"
              type="text"
              placeholder="Contact Admin on Telegram"
              value={settings.admin_telegram_label ?? ""}
              onChange={(e) => update("admin_telegram_label", e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
      </Section>

      {/* Milestones */}
      <Section title="Milestones" subtitle="Ordered by lot. The highest lot triggers Bali audit." icon={Coins}>
        <div className="space-y-3">
          {(settings.milestones || []).map((m, i) => (
            <div key={i} data-testid={`milestone-row-${i}`} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-3">
                <label className="block text-[10px] uppercase text-gray-500 mb-1">Lot</label>
                <input
                  data-testid={`milestone-lot-${i}`}
                  type="number"
                  value={m.lot}
                  onChange={(e) => updateMilestone(i, { lot: Number(e.target.value) })}
                  className={inputCls}
                />
              </div>
              <div className="col-span-3">
                <label className="block text-[10px] uppercase text-gray-500 mb-1">Reward $</label>
                <input
                  data-testid={`milestone-reward-${i}`}
                  type="number"
                  step="0.01"
                  value={m.reward ?? 0}
                  onChange={(e) => updateMilestone(i, { reward: Number(e.target.value) })}
                  className={inputCls}
                />
              </div>
              <div className="col-span-5">
                <label className="block text-[10px] uppercase text-gray-500 mb-1">Label</label>
                <input
                  data-testid={`milestone-label-${i}`}
                  type="text"
                  placeholder="e.g. Milestone 1 · $100"
                  value={m.label || ""}
                  onChange={(e) => updateMilestone(i, { label: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="col-span-1 flex items-end h-full">
                <button
                  data-testid={`milestone-remove-${i}`}
                  onClick={() => removeMilestone(i)}
                  className="p-2 rounded-md text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="Remove"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
          <button
            data-testid="milestone-add-btn"
            onClick={addMilestone}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-white/10 text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            <Plus size={14} /> Add milestone
          </button>
        </div>
      </Section>

      {/* Campaign symbols */}
      <Section
        title="Campaign Symbols"
        subtitle="Only trades whose symbol contains one of these keywords count for the milestone. Case-insensitive substring match."
        icon={Tag}
      >
        <Field label="Symbol Keywords (comma separated)" hint="Example: XAG, SILVER, XAG/USD">
          <input
            data-testid="setting-symbols"
            type="text"
            value={(settings.campaign_symbol_keywords || []).join(", ")}
            onChange={(e) => update("campaign_symbol_keywords", e.target.value.split(",").map((s) => s.trim()))}
            className={inputCls}
          />
        </Field>
      </Section>

      <div className="text-[11px] text-gray-500 font-mono">
        Last updated: {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : "—"}
      </div>
    </div>
  );
}
